import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import { useStore } from "@/hooks/useStore";
import { Panel, PageTitle } from "@/components/ui/Panel";
import { Button } from "@/components/ui/Button";
import { Select } from "@/components/ui/Input";
import { toast } from "sonner";

interface Lobby {
  id: string;
  name: string;
  host_id: string;
  is_public: boolean;
  lobby_key: string;
  status: string;
}

interface LobbyPlayer {
  lobby_id: string;
  user_id: string;
  slot: number;
  deck_id: string | null;
  is_ready: boolean;
}

export function PlaytestLobby() {
  const { id } = useParams();
  const { user } = useAuth();
  const { state } = useStore();
  const navigate = useNavigate();
  const [lobby, setLobby] = useState<Lobby | null>(null);
  const [players, setPlayers] = useState<LobbyPlayer[]>([]);

  async function load() {
    if (!id) return;
    const [{ data: lob }, { data: ps }] = await Promise.all([
      supabase.from("lobbies").select("*").eq("id", id).maybeSingle(),
      supabase.from("lobby_players").select("*").eq("lobby_id", id).order("slot"),
    ]);
    setLobby(lob as Lobby | null);
    setPlayers((ps ?? []) as LobbyPlayer[]);
  }

  useEffect(() => {
    load();
    if (!id) return;
    const channel = supabase
      .channel(`lobby:${id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "lobby_players", filter: `lobby_id=eq.${id}` }, load)
      .on("postgres_changes", { event: "*", schema: "public", table: "lobbies", filter: `id=eq.${id}` }, load)
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [id]);

  async function join(slot: number) {
    if (!user || !id) return;
    const { error } = await supabase.from("lobby_players").upsert({
      lobby_id: id, user_id: user.id, slot, is_ready: false,
    }, { onConflict: "lobby_id,slot" });
    if (error) toast.error(error.message);
  }

  async function setDeck(deckId: string) {
    if (!user || !id) return;
    const me = players.find((p) => p.user_id === user.id);
    if (!me) return;
    await supabase.from("lobby_players").update({ deck_id: deckId }).eq("lobby_id", id).eq("user_id", user.id);
  }

  async function toggleReady() {
    if (!user || !id) return;
    const me = players.find((p) => p.user_id === user.id);
    if (!me) return;
    await supabase.from("lobby_players").update({ is_ready: !me.is_ready }).eq("lobby_id", id).eq("user_id", user.id);
  }

  async function start() {
    if (!lobby || !id) return;
    await supabase.from("lobbies").update({ status: "in_progress" }).eq("id", id);
    navigate(`/playtest/online/${id}`);
  }

  if (!lobby) return <Panel><p className="text-sm text-[#64748b]">Loading lobby…</p></Panel>;

  const me = players.find((p) => p.user_id === user?.id);
  const isHost = user?.id === lobby.host_id;
  const allReady = players.length === 2 && players.every((p) => p.is_ready && p.deck_id);

  return (
    <div className="space-y-4">
      <PageTitle>{lobby.name}</PageTitle>
      <Panel>
        <p className="text-sm">Status: <span className="text-amber-soft">{lobby.status}</span></p>
        <p className="text-sm">Join key: <span className="font-mono">{lobby.lobby_key}</span></p>
      </Panel>

      <div className="grid gap-3 sm:grid-cols-2">
        {[1, 2].map((slot) => {
          const occupant = players.find((p) => p.slot === slot);
          return (
            <Panel key={slot}>
              <h3 className="font-display text-base font-semibold">Slot {slot}</h3>
              {occupant ? (
                <div className="mt-2 text-sm">
                  <p className="text-xs text-[#64748b]">{occupant.user_id.slice(0, 8)}</p>
                  <p>Deck: {state.decks.find((d) => d.id === occupant.deck_id)?.deckName ?? "—"}</p>
                  <p>Ready: {occupant.is_ready ? "✓" : "—"}</p>
                </div>
              ) : (
                <p className="text-xs text-[#64748b]">empty</p>
              )}
              {!occupant && user && !me && (
                <Button className="mt-2" onClick={() => join(slot)}>Take seat</Button>
              )}
            </Panel>
          );
        })}
      </div>

      {me && (
        <Panel className="space-y-3">
          <h3 className="font-display text-sm font-semibold">Your loadout</h3>
          <Select value={me.deck_id ?? ""} onChange={(e) => setDeck(e.target.value)}>
            <option value="">— select deck —</option>
            {state.decks.map((d) => <option key={d.id} value={d.id}>{d.deckName}</option>)}
          </Select>
          <Button onClick={toggleReady} variant={me.is_ready ? "secondary" : "primary"}>
            {me.is_ready ? "Cancel ready" : "Ready"}
          </Button>
        </Panel>
      )}

      {isHost && (
        <Button disabled={!allReady} onClick={start}>Start match</Button>
      )}
    </div>
  );
}
